import { collection, getDocs, getDoc, doc, setDoc, query, orderBy, onSnapshot, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger.ts';

// ========================================
// Logger 설정 - TeamFirebaseService
// ========================================
const logger = createLogger('TeamFirebaseService');
const DEBUG = false;

// 팀 정보 가져오기
export const getTeams = async () => {
  const teamsCol = collection(db, 'teams');
  const q = query(teamsCol, orderBy("name"));
  const teamSnapshot = await getDocs(q);
  return teamSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// 팀 정보 저장하기
export const saveTeams = async (teams: any[]) => {
  try {
    const teamsCol = collection(db, 'teams');

    const teamSnapshot = await getDocs(teamsCol);
    const existingTeams = teamSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const teamNameMap = new Map();
    existingTeams.forEach((team: any) => {
      if (team.name) {
        teamNameMap.set(team.name.toLowerCase(), team.id);
      }
    });

    for (const team of teams) {
      const normalizedName = team.name ? team.name.trim() : "";
      if (!normalizedName) continue;

      if (team.id) {
        const teamRef = doc(db, 'teams', team.id);
        await setDoc(teamRef, {
          ...team,
          name: normalizedName,
        }, { merge: true });
      } else {
        const existingId = teamNameMap.get(normalizedName.toLowerCase());

        if (existingId) {
          const teamRef = doc(db, 'teams', existingId);
          await setDoc(teamRef, {
            ...team,
            name: normalizedName,
            id: existingId
          }, { merge: true });
        } else {
          const newId = `team_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          const teamRef = doc(db, 'teams', newId);
          await setDoc(teamRef, {
            ...team,
            name: normalizedName,
            id: newId
          });
        }
      }
    }
    return true;
  } catch (error) {
    DEBUG && logger.error('팀 데이터 저장 중 오류 발생:', error);
    throw error;
  }
};

// 팀 삭제하기
export const deleteTeam = async (teamId: string) => {
  try {
    if (!teamId) {
      throw new Error('팀 ID가 필요합니다.');
    }

    DEBUG && logger.info(`[Firebase Service] 팀 삭제 시작: ${teamId}`);

    const teamRef = doc(db, 'teams', teamId);
    await deleteDoc(teamRef);

    DEBUG && logger.info(`[Firebase Service] 팀 삭제 완료: ${teamId}`);
    return true;
  } catch (error) {
    DEBUG && logger.error('팀 삭제 중 오류 발생:', error);
    throw error;
  }
};

// 실시간 팀 데이터 구독 (선택적)
export const subscribeToTeams = (teamsCallback: (data: string[]) => void, budgetCallback: (data: any) => void) => {
  const teamsCol = collection(db, 'teams');
  const budgetDocRef = doc(db, 'budget', 'currentBudget');

  const unsubscribeTeams = onSnapshot(teamsCol, (snapshot) => {
    const teamsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    teamsCallback(teamsData.map((team: any) => team.name));
  });

  const unsubscribeBudget = onSnapshot(budgetDocRef, (doc) => {
    if (doc.exists()) {
      budgetCallback(doc.data());
    }
  });

  return () => {
    unsubscribeTeams();
    unsubscribeBudget();
  };
};

// 팀 이름 매핑 정보 조회
export const getTeamNameMapping = async () => {
  try {
    const mappingDoc = doc(db, 'config', 'teamNameMapping');
    const docSnap = await getDoc(mappingDoc);

    if (docSnap.exists()) {
      return docSnap.data().mapping || {};
    }
    return {};
  } catch (error) {
    DEBUG && logger.error(' 팀 이름 매핑 조회 실패:', error);
    return {};
  }
};

// 팀 이름 매핑 저장
export const saveTeamNameMapping = async (mapping: any) => {
  try {
    const mappingDoc = doc(db, 'config', 'teamNameMapping');
    await setDoc(mappingDoc, { mapping, updatedAt: new Date() }, { merge: true });
    DEBUG && logger.info(' 팀 이름 매핑 저장 완료');
  } catch (error) {
    DEBUG && logger.error(' 팀 이름 매핑 저장 실패:', error);
    throw error;
  }
};

// 팀 ID와 현재 이름 매핑하여 저장
export const updateTeamNameInMapping = async (teamId: string, currentName: string, previousName: string | null = null) => {
  try {
    const currentMapping: any = await getTeamNameMapping();

    if (!currentMapping[teamId]) {
      currentMapping[teamId] = {
        current: currentName,
        history: previousName ? [previousName] : []
      };
    } else {
      if (previousName && currentMapping[teamId].current !== currentName) {
        if (!currentMapping[teamId].history.includes(currentMapping[teamId].current)) {
          currentMapping[teamId].history.push(currentMapping[teamId].current);
        }
      }
      currentMapping[teamId].current = currentName;
    }

    await saveTeamNameMapping(currentMapping);
  } catch (error) {
    DEBUG && logger.error(' 팀 이름 매핑 업데이트 실패:', error);
    throw error;
  }
};
